/**
 * Estatísticas pós-jogo do bolão (entretenimento + acompanhamento).
 *
 * Função PURA: recebe a lista de jogos já com rótulos prontos (times
 * resolvidos), os palpites de cada participante e os resultados reais,
 * e devolve rankings/curiosidades. Não acessa estado nem rede, então
 * não tem como "quebrar" o app — se faltar dado, simplesmente ignora
 * aquele jogo/participante.
 *
 * Calcula:
 *  - Resumo geral (jogos com resultado, placares exatos no total)
 *  - Top cravadores (quem mais acertou placar exato)
 *  - Acertos por jogo (quantos e QUEM cravou o placar exato; quantos e
 *    quem acertou o vencedor/empate)
 *  - Maior goleada real, jogo com mais gols
 */
import {
  acertouVencedorMataMata,
  placarValido,
  resultado1x2,
  type LadoPlacarMM,
  type Placar,
  type PlacaresMap,
} from './copaCalculo';

export interface JogoInfoStats {
  id: string;
  /** Ex.: "Brasil 🇧🇷 x Sérvia" — pronto para exibir. */
  rotulo: string;
  /** Ex.: "Grupo C · Rodada 1" ou "Oitavas · Jogo 89". */
  contexto: string;
  ehMataMata: boolean;
  /** Vencedor de pênaltis (só MM, quando o placar real empatou). */
  vencedorPenaltis?: LadoPlacarMM | null;
}

export interface ParticipanteStats {
  id: string;
  nome: string;
  palpites: PlacaresMap;
}

export interface LinhaCravador {
  id: string;
  nome: string;
  placaresExatos: number;
  acertosVencedor: number;
  /** Palpites comparados com resultado real (jogos já encerrados). */
  palpitesAvaliados: number;
}

/** Resumo de acertos de UM jogo específico (já com resultado real). */
export interface AcertoJogo {
  jogoId: string;
  rotulo: string;
  contexto: string;
  ehMataMata: boolean;
  real: Placar;
  vencedorPenaltis?: LadoPlacarMM | null;
  /** Total de participantes que palpitaram nesse jogo. */
  totalPalpitou: number;
  /** Nomes de quem acertou o placar exato (cravou). */
  cravaramExato: string[];
  /** Nomes de quem acertou o vencedor/empate mas NÃO cravou o placar. */
  acertaramSoVencedor: string[];
}

export interface EstatisticasPosJogo {
  totalJogosComResultado: number;
  totalPalpitesAvaliados: number;
  placaresExatosTotais: number;
  topCravadores: LinhaCravador[];
  /** Menor número de placares exatos; todos os empatados aparecem aqui. */
  menosCravadores: LinhaCravador[];
  acertosPorJogo: AcertoJogo[];
  maiorGoleada: { jogoId: string; rotulo: string; contexto: string; real: Placar; saldo: number } | null;
  jogoMaisGols: { jogoId: string; rotulo: string; contexto: string; real: Placar; gols: number } | null;
}

function ehPlacarExato(palpite: Placar, real: Placar): boolean {
  return palpite.mandante === real.mandante && palpite.visitante === real.visitante;
}

function acertouVencedor(jogo: JogoInfoStats, palpite: Placar, real: Placar): boolean {
  if (jogo.ehMataMata) {
    return acertouVencedorMataMata(palpite, real, jogo.vencedorPenaltis);
  }
  return resultado1x2(palpite) === resultado1x2(real);
}

export function calcularEstatisticasPosJogo(
  jogos: JogoInfoStats[],
  participantes: ParticipanteStats[],
  resultadosReais: PlacaresMap
): EstatisticasPosJogo {
  const jogosComResultado = jogos.filter((j) => placarValido(resultadosReais[j.id]));

  const cravadorPorId = new Map<string, LinhaCravador>();
  participantes.forEach((p) => {
    cravadorPorId.set(p.id, {
      id: p.id,
      nome: p.nome,
      placaresExatos: 0,
      acertosVencedor: 0,
      palpitesAvaliados: 0,
    });
  });

  let totalPalpitesAvaliados = 0;
  let placaresExatosTotais = 0;

  const acertosPorJogo: AcertoJogo[] = [];

  jogosComResultado.forEach((jogo) => {
    const real = resultadosReais[jogo.id] as Placar;
    let totalPalpitou = 0;
    const cravaramExato: string[] = [];
    const acertaramSoVencedor: string[] = [];

    participantes.forEach((p) => {
      const palpite = p.palpites[jogo.id];
      if (!placarValido(palpite)) return;
      totalPalpitou += 1;
      totalPalpitesAvaliados += 1;

      const linha = cravadorPorId.get(p.id)!;
      linha.palpitesAvaliados += 1;
      const exato = ehPlacarExato(palpite, real);
      const venc = acertouVencedor(jogo, palpite, real);
      if (exato) {
        placaresExatosTotais += 1;
        linha.placaresExatos += 1;
        cravaramExato.push(p.nome);
      } else if (venc) {
        linha.acertosVencedor += 1;
        acertaramSoVencedor.push(p.nome);
      }
    });

    if (totalPalpitou > 0) {
      cravaramExato.sort((a, b) => a.localeCompare(b, 'pt-BR'));
      acertaramSoVencedor.sort((a, b) => a.localeCompare(b, 'pt-BR'));
      acertosPorJogo.push({
        jogoId: jogo.id,
        rotulo: jogo.rotulo,
        contexto: jogo.contexto,
        ehMataMata: jogo.ehMataMata,
        real,
        vencedorPenaltis: jogo.vencedorPenaltis ?? null,
        totalPalpitou,
        cravaramExato,
        acertaramSoVencedor,
      });
    }
  });

  const topCravadores = Array.from(cravadorPorId.values())
    .filter((c) => c.placaresExatos > 0 || c.acertosVencedor > 0)
    .sort((a, b) => {
      if (b.placaresExatos !== a.placaresExatos) return b.placaresExatos - a.placaresExatos;
      if (b.acertosVencedor !== a.acertosVencedor) return b.acertosVencedor - a.acertosVencedor;
      return a.nome.localeCompare(b.nome, 'pt-BR');
    });

  const elegiveisMenos = Array.from(cravadorPorId.values()).filter((c) => c.palpitesAvaliados > 0);
  const minPlacaresExatos =
    elegiveisMenos.length > 0 ? Math.min(...elegiveisMenos.map((c) => c.placaresExatos)) : 0;
  const menosCravadores = elegiveisMenos
    .filter((c) => c.placaresExatos === minPlacaresExatos)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  // Maior goleada (saldo) e jogo com mais gols.
  let maiorGoleada: EstatisticasPosJogo['maiorGoleada'] = null;
  let jogoMaisGols: EstatisticasPosJogo['jogoMaisGols'] = null;
  jogosComResultado.forEach((jogo) => {
    const real = resultadosReais[jogo.id] as Placar;
    const saldo = Math.abs(real.mandante - real.visitante);
    const gols = real.mandante + real.visitante;
    if (!maiorGoleada || saldo > maiorGoleada.saldo) {
      maiorGoleada = {
        jogoId: jogo.id,
        rotulo: jogo.rotulo,
        contexto: jogo.contexto,
        real,
        saldo,
      };
    }
    if (!jogoMaisGols || gols > jogoMaisGols.gols) {
      jogoMaisGols = {
        jogoId: jogo.id,
        rotulo: jogo.rotulo,
        contexto: jogo.contexto,
        real,
        gols,
      };
    }
  });

  return {
    totalJogosComResultado: jogosComResultado.length,
    totalPalpitesAvaliados,
    placaresExatosTotais,
    topCravadores,
    menosCravadores,
    acertosPorJogo,
    maiorGoleada,
    jogoMaisGols,
  };
}
